import React, { Component } from "react";
import { Link, Redirect } from "react-router-dom";
import cx from "classnames";
import SliderContext from "../SliderView/MainSlider/context";
import ShowDetailsButton from "../SliderView/MainSlider/ShowDetailsButton";
import Mark from "../SliderView/MainSlider/Mark";
import "../SliderView/MainSlider/Item.scss";
import "./ContentCard.css";
import Helper from "../Helper/helper";
import VideoAccessBadge from "../Helper/VideoAccessBadge";
import {
  resolveMediaBackground,
  resolveMediaUrl,
  handleMediaImgError,
  DEFAULT_MEDIA_PLACEHOLDER,
} from "../Helper/mediaUrl";

const DATE_OPTIONS = { year: "numeric", month: "short" };

/**
 * Type-aware homepage / row card for unified Content Items.
 * Videos keep existing slider UX; Channels show live badge + now/next + progress.
 */
class ContentCard extends Helper {
  state = {
    playButtonClicked: false,
    videoDetailsFirst: null,
  };

  contentType(item) {
    return String((item && item.content_type) || "video").toLowerCase();
  }

  detailPath(item) {
    if (item && item.detail_route) return item.detail_route;
    if (item && item.playback && item.playback.tune_url) return item.playback.tune_url;
    const type = this.contentType(item);
    if (type === "channel") {
      return "/channels/" + (item.slug || item.unique_id || item.channel_id || "");
    }
    if (type === "livestream") {
      return "/live/" + (item.slug || item.unique_id || "");
    }
    if (type === "playlist") {
      return "/playlist/" + (item.slug || item.unique_id || "");
    }
    if (type === "sub_category") {
      const slug = item.slug && String(item.slug).trim();
      return "/sub-category/" + (slug || item.sub_category_id || item.id || "");
    }
    const vid = item.admin_video_id || item.contentable_id || item.id;
    return vid ? "/video/" + vid : "/home";
  }

  handlePlayVideo = async (event) => {
    event.preventDefault();
    const item = this.props.movie || this.props.item;
    const type = this.contentType(item);
    if (type === "channel") {
      this.setState({ playButtonClicked: true, redirectChannel: this.detailPath(item) });
      return;
    }
    if (type !== "video") {
      this.setState({ playButtonClicked: true, redirectChannel: this.detailPath(item) });
      return;
    }
    const inputData = {
      admin_video_id: item.admin_video_id || item.contentable_id,
      skip: 0,
    };
    var details = await this.onlySingleVideoFirst(inputData);
    this.redirectStatus(details);
    this.setState({ playButtonClicked: true });
  };

  channelArtwork(item) {
    return (
      item.banner ||
      item.thumbnail ||
      item.default_image ||
      (item.now_playing && item.now_playing.thumbnail) ||
      null
    );
  }

  channelLogo(item) {
    return item.logo || item.thumbnail || item.default_image || null;
  }

  nowTitle(item) {
    const now = item && item.now_playing;
    if (!now) return null;
    return now.title || now.name || null;
  }

  nextTitle(item) {
    const next = item && item.next_program;
    if (!next) return null;
    return next.title || next.name || null;
  }

  renderThumbMedia(art) {
    return (
      <img
        className="item__media"
        src={resolveMediaUrl(art, DEFAULT_MEDIA_PLACEHOLDER)}
        alt=""
        aria-hidden="true"
        draggable={false}
        onError={handleMediaImgError}
      />
    );
  }

  renderChannelCard(item, ctx) {
    const isActive =
      ctx.currentSlide &&
      (ctx.currentSlide.content_item_id === item.content_item_id ||
        ctx.currentSlide.channel_id === item.channel_id);
    const art = this.channelArtwork(item);
    const logo = this.channelLogo(item);
    const nowTitle = this.nowTitle(item);
    const nextTitle = this.nextTitle(item);
    const isLive = item.is_live === true || item.live_badge === true || !!nowTitle;
    const progress =
      typeof item.progress_percent === "number" ? item.progress_percent : null;
    const desc = item.description || "";
    const path = this.detailPath(item);
    const openLabel = "Open " + (item.title || "channel");

    return (
      <div
        ref={ctx.elementRef}
        className={cx("item sliderthumb-text content-card content-card--channel", {
          "item--open": isActive,
        })}
        style={{
          backgroundImage: resolveMediaBackground(art, DEFAULT_MEDIA_PLACEHOLDER),
        }}
      >
        {this.renderThumbMedia(art)}
        {/*
          No ShowDetailsButton on channel cards. Two links, one destination:
          .close-overlay serves the resting tile; .content-card__link fills the
          hover overlay so the whole thumbnail navigates (not only Play).
        */}
        <Link to={path} className="close-overlay" aria-label={openLabel} />
        {isLive ? (
          <span className="content-card__live-badge" aria-hidden="true">
            LIVE
          </span>
        ) : (
          <span className="content-card__type-badge" aria-hidden="true">
            Channel
          </span>
        )}
        {logo && art && logo !== art ? (
          <img
            className="content-card__logo"
            src={logo}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : null}
        <div className="width-100 slider-content-box">
          <Link
            to={path}
            className="content-card__link"
            onClick={this.handlePlayVideo}
            aria-label={openLabel}
          >
            <div className="thumb-playicon" aria-hidden="true">
              <i className="fas fa-play" />
            </div>
            <h4 className="thumb-title">{item.title}</h4>
            <h5 className="thumb-details content-card__meta">
              {nowTitle ? (
                <span className="content-card__now">Now: {nowTitle}</span>
              ) : (
                <span className="content-card__now content-card__now--idle">
                  Off-air / Schedule
                </span>
              )}
              {nextTitle ? (
                <span className="content-card__next">Next: {nextTitle}</span>
              ) : null}
            </h5>
            {progress != null ? (
              <div
                className="content-card__progress"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  className="content-card__progress-bar"
                  style={{ width: progress + "%" }}
                />
              </div>
            ) : null}
            {desc ? <p className="thumb-desc">{desc}</p> : null}
          </Link>
          {/* Channels have no video detail panel — whole card opens the channel. */}
        </div>
        {isActive && <Mark />}
      </div>
    );
  }

  renderVideoCard(item, ctx) {
    const isActive =
      ctx.currentSlide &&
      ctx.currentSlide.admin_video_id === item.admin_video_id;

    return (
      <div
        ref={ctx.elementRef}
        className={cx("item sliderthumb-text content-card content-card--video", {
          "item--open": isActive,
        })}
        style={{
          backgroundImage: resolveMediaBackground(
            item.default_image || item.thumbnail,
            DEFAULT_MEDIA_PLACEHOLDER
          ),
        }}
      >
        {this.renderThumbMedia(item.default_image || item.thumbnail)}
        <a onClick={() => ctx.onSelectSlide(item)} className="close-overlay" />
        <VideoAccessBadge item={item} className="video-access-badge--card" />
        <div className="width-100 slider-content-box">
          <Link to="#" onClick={this.handlePlayVideo}>
            <div className="thumb-playicon">
              <i className="fas fa-play" />
            </div>
          </Link>
          <h4 className="thumb-title">{item.title}</h4>
          <h5 className="thumb-details">
            {item.publish_time ? (
              <span className="green-clr">
                {new Date(item.publish_time).toLocaleDateString("en-US", DATE_OPTIONS)}
              </span>
            ) : null}
            {item.age != null ? (
              <span className="grey-box">
                {item.age}
                <i className="fas fa-plus small" /> / {item.watch_count || 0}{" "}
                <span className="small">Views</span>
              </span>
            ) : null}
          </h5>
          <p className="thumb-desc">{item.description}</p>
          <ShowDetailsButton onClick={() => ctx.onSelectSlide(item)} />
        </div>
        {isActive && <Mark />}
      </div>
    );
  }

  /**
   * Home category rows present Subcategories; the whole card opens the
   * Subcategory page, where videos, episodes and seasons are browsed.
   */
  renderSubCategoryCard(item, ctx) {
    const art = item.thumbnail || item.default_image || item.banner;
    const path = this.detailPath(item);

    return (
      <div
        ref={ctx.elementRef}
        className={cx(
          "item sliderthumb-text content-card content-card--sub-category"
        )}
        style={{
          backgroundImage: resolveMediaBackground(art, DEFAULT_MEDIA_PLACEHOLDER),
        }}
      >
        {this.renderThumbMedia(art)}
        {/*
          Two links, one destination: .close-overlay serves the resting tile and
          .content-card__link fills the hover overlay, which covers the tile.
        */}
        <Link to={path} className="close-overlay" />
        <div className="width-100 slider-content-box">
          <Link to={path} className="content-card__link">
            <h4 className="thumb-title">{item.title}</h4>
            {item.description ? (
              <p className="thumb-desc">{item.description}</p>
            ) : null}
          </Link>
        </div>
      </div>
    );
  }

  renderStubCard(item, ctx) {
    const type = this.contentType(item);
    const art = item.banner || item.thumbnail || item.default_image;
    return (
      <div
        ref={ctx.elementRef}
        className={cx("item sliderthumb-text content-card content-card--stub")}
        style={{
          backgroundImage: resolveMediaBackground(art, DEFAULT_MEDIA_PLACEHOLDER),
        }}
      >
        {this.renderThumbMedia(art)}
        <Link to={this.detailPath(item)} className="close-overlay" />
        <span className="content-card__type-badge">{type}</span>
        <div className="width-100 slider-content-box">
          <Link to={this.detailPath(item)} className="content-card__link">
            <div className="thumb-playicon">
              <i className="fas fa-play" />
            </div>
            <h4 className="thumb-title">{item.title}</h4>
            <p className="thumb-desc">{item.description}</p>
          </Link>
        </div>
      </div>
    );
  }

  render() {
    const item = this.props.movie || this.props.item || {};
    const type = this.contentType(item);

    if (this.state.playButtonClicked) {
      if (this.state.redirectChannel) {
        return <Redirect to={this.state.redirectChannel} />;
      }
      const returnToVideo = this.renderRedirectPage(this.state.videoDetailsFirst);
      if (returnToVideo != null) {
        return returnToVideo;
      }
    }

    return (
      <SliderContext.Consumer>
        {(ctx) => {
          if (type === "sub_category") return this.renderSubCategoryCard(item, ctx);
          if (type === "channel") return this.renderChannelCard(item, ctx);
          if (type === "video" || !type) return this.renderVideoCard(item, ctx);
          return this.renderStubCard(item, ctx);
        }}
      </SliderContext.Consumer>
    );
  }
}

export default ContentCard;
